// ============================================================================
// docAssinadoLog.ts — LOG PERSISTENTE de assinaturas feitas neste aparelho.
// ----------------------------------------------------------------------------
// Problema que resolve: o app avisava "assinatura pendente" (modal da Home) para
// documento JÁ ASSINADO que só aguardava o backend gerar o _Ass.pdf (T-ARC lento
// ou assinatura offline). A proteção antiga era um grace de 3min por RELÓGIO —
// relógio não distingue "backend lento" de "admin reverteu", e avaliado sobre
// userData defasado (remount da Home) produzia o pisca.
//
// Desenho:
//  • Cada assinatura grava aqui `${hashEmpresa}:${docId}` → { assinadoEmMs,
//    entregueEmMs, data_log }. Chaveado por EMPRESA porque o
//    id_adm_contra_cheque_colaborador é auto-increment POR TENANT — sem o hash,
//    trocar de empresa poderia suprimir documento ALHEIO com o mesmo número.
//  • O log NUNCA é limpo em logout/troca de empresa (requisito). Entrada some
//    quando o _Ass.pdf CONFIRMA (aí a verdade é o servidor) ou pela poda (cap).
//  • REVERT só por CONFIRMAÇÃO FRESCA: o doc com entrada no log só volta a
//    PENDENTE quando um /iniciar REAL (carimbo userdata_fetched_at), posterior à
//    ENTREGA + JANELA, ainda vier sem o _Ass.pdf. Nada de timer cego: userData
//    velho/offline/API doente nunca cobram falso; relógio errado é irrelevante
//    (os dois carimbos saem do MESMO relógio do aparelho).
// ============================================================================

import AsyncStorage from "@/platform/async-storage";
import { readJSONFile } from "./FileStorage";

const LOG_KEY = "@TirvuMais:docs_assinados_log";
// Mapa hash → ms do último /iniciar REAL daquele TENANT (fetch da empresa A não pode
// validar revert de doc da empresa B).
const FETCHED_AT_KEY = "@TirvuMais:userdata_fetched_at_por_tenant";
const HASH_ATUAL_KEY = "@TirvuMais:empresa_hash_atual";

/** Janela pós-ENTREGA p/ o backend gerar o _Ass.pdf antes de um fetch fresco poder cobrar. */
export const DOC_REVERT_JANELA_MS = 300000; // 5 min

/**
 * Janela pós-CONFIRMAÇÃO (o app JÁ VIU o _Ass.pdf): o nome_arquivo OSCILA entre
 * réplicas do t-credentials (cache de DataContent de ~45s POR RÉPLICA) — uma réplica
 * ainda serve o nome ANTIGO depois de outra já ter mostrado o _Ass.pdf. Ausência do
 * _Ass.pdf só conta como REVERT REAL se o fetch for posterior a confirmadoEm + esta
 * folga; antes disso é oscilação → continua ASSINADO (2 checks, estável). Sem isso:
 * 2 checks → pendente (modal!) → assinado de novo, a cada troca de réplica.
 */
export const DOC_OSCILACAO_JANELA_MS = 180000; // 3 min (folga sobre os 45s do cache)

/** Cap do log (poda pelo assinadoEmMs mais antigo) — não cresce para sempre. */
const MAX_ENTRADAS = 200;

export interface DocAssinadoEntry {
  assinadoEmMs: number; // Date.now() no ATO da assinatura (ms — nunca string ISO local)
  entregueEmMs: number; // Date.now() quando a assinatura CHEGOU ao servidor (0 = ainda na fila)
  data_log: string; // data/hora legível da assinatura (auditoria/exibição)
  confirmadoEmMs?: number; // Date.now() quando o app VIU o _Ass.pdf (confirmação STICKY)
}

function agoraLegivel(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

async function lerMapa(): Promise<Record<string, DocAssinadoEntry>> {
  try {
    const raw = await AsyncStorage.getItem(LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

async function gravarMapa(map: Record<string, DocAssinadoEntry>): Promise<void> {
  try {
    await AsyncStorage.setItem(LOG_KEY, JSON.stringify(map));
  } catch {
    /* best-effort */
  }
}

// Cache de MÓDULO do hash da empresa atual: o snapshot de documentos roda a cada
// foco/tabPress/userData (Home + BottomBar + applyLocalDocStatus) — parsear o
// userData.json INTEIRO (centenas de KB) a cada chamada custaria caro no JS thread.
// Atualizado por definirEmpresaHashAtual (todo /iniciar real) e persistido numa
// chave PEQUENA própria; o userData.json é só o fallback de 1ª execução/migração.
let _hashCache: string | null = null;

/** Registra o hash da empresa ATUAL (chamado a cada /iniciar real bem-sucedido). */
export async function definirEmpresaHashAtual(hash: any): Promise<void> {
  const h = String(hash ?? "").trim().toUpperCase();
  if (!h) return;
  _hashCache = h;
  try {
    await AsyncStorage.setItem(HASH_ATUAL_KEY, h);
  } catch {
    /* best-effort */
  }
}

/** Hash da empresa ATUAL. '' quando indisponível (não indexa). */
export async function empresaHashAtual(): Promise<string> {
  if (_hashCache) return _hashCache;
  try {
    const k = await AsyncStorage.getItem(HASH_ATUAL_KEY);
    if (k) {
      _hashCache = k;
      return k;
    }
  } catch {}
  // Fallback (1ª execução pós-deploy): lê o userData.json UMA vez e semeia o cache.
  try {
    const ud: any = await readJSONFile("userData.json");
    const h = String(ud?.hash ?? ud?.done?.hash ?? "").trim().toUpperCase();
    if (h) {
      _hashCache = h;
      try {
        await AsyncStorage.setItem(HASH_ATUAL_KEY, h);
      } catch {}
    }
    return h;
  } catch {
    return "";
  }
}

function chave(hash: string, docId: any): string {
  return `${hash}:${String(docId)}`;
}

/** Poda: mantém as MAX_ENTRADAS mais recentes (por assinadoEmMs). */
function podar(map: Record<string, DocAssinadoEntry>): Record<string, DocAssinadoEntry> {
  const keys = Object.keys(map);
  if (keys.length <= MAX_ENTRADAS) return map;
  const ordenadas = keys.sort(
    (a, b) => (map[b]?.assinadoEmMs || 0) - (map[a]?.assinadoEmMs || 0),
  );
  const novo: Record<string, DocAssinadoEntry> = {};
  for (const k of ordenadas.slice(0, MAX_ENTRADAS)) novo[k] = map[k];
  return novo;
}

/**
 * Registra a assinatura (chamado em TODOS os ramos do signDocument).
 * `entregue=true` quando a API já aceitou (online-sucesso) — senão fica 0 e o
 * drain da fila carimba depois via marcarDocEntregueLog.
 */
export async function marcarDocAssinadoLog(
  docId: any,
  opts?: { entregue?: boolean },
): Promise<void> {
  if (docId == null || String(docId).trim() === "") return;
  const hash = await empresaHashAtual();
  if (!hash) return; // sem empresa resolvida não indexa (evita chave lixo)
  const map = await lerMapa();
  const k = chave(hash, docId);
  const agora = Date.now();
  map[k] = {
    assinadoEmMs: agora,
    entregueEmMs: opts?.entregue ? agora : 0,
    data_log: agoraLegivel(),
  };
  await gravarMapa(podar(map));
}

/** Carimba a ENTREGA (drain da fila SIGNATURE). Fallback: sufixo `:${docId}` sem entrega. */
export async function marcarDocEntregueLog(docId: any): Promise<void> {
  if (docId == null || String(docId).trim() === "") return;
  const map = await lerMapa();
  const hash = await empresaHashAtual();
  const direta = hash ? map[chave(hash, docId)] : undefined;
  const agora = Date.now();
  if (direta) {
    direta.entregueEmMs = agora;
  } else {
    // Item enfileirado antes deste deploy (sem entrada) ou hash indisponível: melhor
    // esforço — carimba entradas do MESMO id ainda não entregues (colisão improvável).
    const sufixo = `:${String(docId)}`;
    for (const k of Object.keys(map)) {
      if (k.endsWith(sufixo) && !map[k].entregueEmMs) map[k].entregueEmMs = agora;
    }
  }
  await gravarMapa(map);
}

/**
 * Marca a CONFIRMAÇÃO (o app viu o _Ass.pdf) — STICKY, NÃO remove a entrada.
 * Remover era o bug do flip-flop: a réplica seguinte (cache velho, nome antigo)
 * achava o doc sem log/marca → PENDENTE + modal → assinado de novo. Com o
 * confirmadoEmMs, a ausência do _Ass.pdf só vira revert após a janela de
 * oscilação. Se a entrada não existe (doc assinado em OUTRO aparelho ou antes
 * deste deploy), CRIA — protege esses docs da oscilação também.
 */
export async function marcarDocConfirmadoLog(docId: any): Promise<void> {
  if (docId == null || String(docId).trim() === "") return;
  const hash = await empresaHashAtual();
  if (!hash) return;
  const map = await lerMapa();
  const k = chave(hash, docId);
  const agora = Date.now();
  const atual = map[k];
  if (atual?.confirmadoEmMs) return; // já confirmado — não regrava a cada refresh
  map[k] = atual
    ? { ...atual, confirmadoEmMs: agora }
    : {
        assinadoEmMs: agora,
        entregueEmMs: agora,
        data_log: agoraLegivel(),
        confirmadoEmMs: agora,
      };
  await gravarMapa(podar(map));
}

/** Log da EMPRESA ATUAL, projetado por docId cru (para o snapshot/resolver). */
export async function lerLogAssinadosDoTenant(): Promise<Record<string, DocAssinadoEntry>> {
  const hash = await empresaHashAtual();
  if (!hash) return {};
  const map = await lerMapa();
  const prefixo = `${hash}:`;
  const out: Record<string, DocAssinadoEntry> = {};
  for (const k of Object.keys(map)) {
    if (k.startsWith(prefixo)) out[k.slice(prefixo.length)] = map[k];
  }
  return out;
}

/**
 * Carimbo de FRESCOR do userData, POR TENANT: gravado SOMENTE quando um /iniciar
 * REAL sucedeu (refreshUserData/syncAll), com o hash da resposta. Hidratação de
 * cache NÃO grava — é isso que impede dado velho de "queimar" a janela e cobrar
 * falso pendente. Por tenant: o fetch da empresa A não valida revert de doc da B.
 */
export async function gravarUserDataFetchedAt(hash?: any): Promise<void> {
  try {
    const h = String(hash ?? "").trim().toUpperCase() || (await empresaHashAtual());
    if (!h) return;
    // Aproveita p/ manter o cache do hash atual em dia (todo /iniciar real passa aqui).
    await definirEmpresaHashAtual(h);
    const raw = await AsyncStorage.getItem(FETCHED_AT_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[h] = Date.now();
    await AsyncStorage.setItem(FETCHED_AT_KEY, JSON.stringify(map));
  } catch {
    /* best-effort */
  }
}

/** Carimbo do TENANT ATUAL (0 = nunca houve /iniciar real → nunca cobra). */
export async function lerUserDataFetchedAt(): Promise<number> {
  try {
    const h = await empresaHashAtual();
    if (!h) return 0;
    const raw = await AsyncStorage.getItem(FETCHED_AT_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const n = Number(map[h] || 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * REGRA COMPARTILHADA (resolver + applyLocalDocStatus — NUNCA divergir):
 * revert só é CONFIRMADO quando um userData FRESCO (fetchedAtMs), posterior à
 * entrega (ou à assinatura, se nunca entregue mas fora da fila) + JANELA, ainda
 * veio sem o _Ass.pdf — E, se o app já VIU o _Ass.pdf (confirmadoEmMs), também
 * posterior à confirmação + janela de OSCILAÇÃO (réplicas com cache defasado
 * servem o nome antigo por ~45s depois do rename; isso NÃO é revert).
 * naFila (SIGNATURE pendente) nunca confirma revert.
 */
export function ehRevertConfirmado(
  entry: DocAssinadoEntry | undefined,
  naFila: boolean,
  fetchedAtMs: number,
): boolean {
  if (!entry || naFila) return false;
  if (entry.confirmadoEmMs) {
    // O _Ass.pdf JÁ EXISTIU (confirmado): a janela de 5min — que existe p/ dar tempo de
    // GERAR o arquivo — não se aplica (geração concluída). Só a folga de OSCILAÇÃO
    // protege: passado ela, ausência do _Ass.pdf num fetch fresco = REVERT REAL do admin
    // → PENDENTE. (Sem este atalho, a janela de entrega — que pode ter sido RE-ANCORADA
    // "agora" quando a entrada foi criada p/ um doc já confirmado — segurava o doc como
    // "Assinado" por 5min após o revert, parecendo que o revert nunca chegava.)
    return fetchedAtMs > entry.confirmadoEmMs + DOC_OSCILACAO_JANELA_MS;
  }
  // Nunca confirmado: dá a janela de GERAÇÃO a partir da entrega (ou da assinatura,
  // se nunca entregue mas fora da fila).
  const baseMs = entry.entregueEmMs || entry.assinadoEmMs;
  return baseMs > 0 && fetchedAtMs > baseMs + DOC_REVERT_JANELA_MS;
}
